import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { submitParseFeedback } from '../../api/client'

/**
 * ParseFeedbackPopover — the parse-feedback surface for a checkin, opened
 * from the "Report" entry in the checkin's actions menu. Thumbs up/down, an
 * optional free-text note, and a submit that lands in parse_feedback_notes
 * attributed to the checkin's latest parse job. The title is "Checkin <id>"
 * (click to copy the id) for bug reports. Everyone sees it; the data is only
 * readable admin-side.
 *
 * Controlled: the parent owns `open` and gets `onClose` on outside click,
 * Escape, or after the post-submit thanks state. The component renders an
 * empty relative anchor when closed, so the popover pops from wherever the
 * parent mounts it (top-right of the checkin header).
 *
 * Motion vocab (CLAUDE.md): buttons use whileHover scale 1.1 / whileTap
 * scale 0.9; the popover pops with the surface spring (scale 0.95→1 / y -8→0)
 * and a top-right transformOrigin, since it hangs off a right-aligned anchor.
 */

const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const
const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const

interface ParseFeedbackPopoverProps {
  checkinId: number
  open: boolean
  onClose: () => void
  /** How long the "Thanks" state stays up before the popover closes. */
  thanksDelayMs?: number
}

const ParseFeedbackPopover: React.FC<ParseFeedbackPopoverProps> = ({
  checkinId,
  open,
  onClose,
  thanksDelayMs = 1200,
}) => {
  const [rating, setRating] = useState<'up' | 'down' | null>(null)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const close = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    onClose()
  }

  // Reset the form whenever the popover closes, and close on any outside
  // click or Escape while it's open (same idiom as SummaryConfigMenu).
  useEffect(() => {
    if (!open) {
      setRating(null)
      setNote('')
      setSubmitting(false)
      setSent(false)
      setError(false)
      setCopiedId(false)
      return
    }
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
  }, [])

  const canSubmit = !submitting && !sent && (rating !== null || note.trim() !== '')

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(false)
    try {
      const trimmed = note.trim()
      await submitParseFeedback(checkinId, {
        rating,
        ...(trimmed !== '' ? { note: trimmed } : {}),
      })
      setSent(true)
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null
        onClose()
      }, thanksDelayMs)
    } catch (e) {
      console.error('Failed to submit parse feedback', e)
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const copyId = () => {
    navigator.clipboard?.writeText(String(checkinId))
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 1200)
  }

  const thumbClass = (active: boolean) =>
    `rounded-md p-1.5 transition-colors cursor-pointer ${
      active
        ? 'bg-amber-100 text-amber-900'
        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
    }`

  return (
    <div ref={rootRef} className="relative">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={SURFACE_SPRING}
            style={{ transformOrigin: 'top right' }}
            role="dialog"
            aria-label="Parse feedback"
            className="absolute right-0 z-50 mt-1 w-64 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
            data-testid="parse-feedback-popover"
          >
            {sent ? (
              // Same color as the composer's transient "Saved" indicator.
              <span className="flex items-center justify-center py-2 text-sm text-green-600">
                Thanks for the feedback!
              </span>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={copyId}
                    title="Click to copy id"
                    className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800"
                    data-testid="parse-feedback-checkin-id"
                  >
                    {copiedId ? 'Copied!' : `Checkin ${checkinId}`}
                  </button>
                  <div className="flex items-center gap-1">
                    <motion.button
                      type="button"
                      onClick={() => setRating((r) => (r === 'up' ? null : 'up'))}
                      aria-label="Thumbs up"
                      aria-pressed={rating === 'up'}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={BUTTON_SPRING}
                      className={thumbClass(rating === 'up')}
                      data-testid="parse-feedback-thumbs-up"
                    >
                      <ThumbsUp className="h-4 w-4" strokeWidth={2} />
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => setRating((r) => (r === 'down' ? null : 'down'))}
                      aria-label="Thumbs down"
                      aria-pressed={rating === 'down'}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={BUTTON_SPRING}
                      className={thumbClass(rating === 'down')}
                      data-testid="parse-feedback-thumbs-down"
                    >
                      <ThumbsDown className="h-4 w-4" strokeWidth={2} />
                    </motion.button>
                  </div>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Notes on analysis?"
                  aria-label="Feedback note"
                  className="w-full resize-none rounded-md border border-gray-200 p-2 text-sm text-gray-700 placeholder-gray-400 focus:border-amber-400 focus:outline-none"
                />
                {error && (
                  <p className="mt-1 text-xs text-rose-600">
                    Couldn't send feedback. Please try again.
                  </p>
                )}
                <div className="mt-2 flex justify-end">
                  <motion.button
                    type="button"
                    onClick={submit}
                    disabled={!canSubmit}
                    whileHover={canSubmit ? { scale: 1.1 } : undefined}
                    whileTap={canSubmit ? { scale: 0.9 } : undefined}
                    transition={BUTTON_SPRING}
                    className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                      canSubmit
                        ? 'cursor-pointer bg-action text-plum hover:bg-action-dark'
                        : 'cursor-default bg-gray-100 text-gray-400'
                    }`}
                    data-testid="parse-feedback-submit"
                  >
                    {submitting ? 'Sending…' : 'Send'}
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ParseFeedbackPopover
